import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Super admin (department_id === 1): full class/division lists.
 * Teacher (Team Member): only assigned class + division combos; auto-select when one.
 */
export default function useTeacherClassScope(http, user) {
    const isSuperAdmin = Number(user?.department_id) === 1;
    const [loadingScope, setLoadingScope] = useState(true);
    const [allClasses, setAllClasses] = useState([]);
    const [allDivisions, setAllDivisions] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedDivision, setSelectedDivision] = useState('');

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoadingScope(true);
            try {
                if (isSuperAdmin) {
                    const [classRes, divRes] = await Promise.all([
                        http.get(`/class?company_id=${user.company_id}`),
                        http.get('/devision'),
                    ]);
                    if (cancelled) return;
                    setAllClasses(Array.isArray(classRes.data?.data) ? classRes.data.data : []);
                    setAllDivisions(Array.isArray(divRes.data?.data) ? divRes.data.data : []);
                    setAssignments([]);
                } else {
                    const mineRes = await http.get(
                        `/teacher_class_assigns/my?login_user=${user.id}`
                    );
                    if (cancelled) return;
                    const rows = Array.isArray(mineRes.data?.data) ? mineRes.data.data : [];
                    setAssignments(rows);
                    // No toast when unassigned — form shows empty options quietly
                    if (rows.length === 1) {
                        setSelectedClass(rows[0].class);
                        setSelectedDivision(rows[0].division);
                    }
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    toast.error('Could not load class / division options');
                }
            } finally {
                if (!cancelled) setLoadingScope(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
        // Intentionally omit `http` — AuthUser creates a new axios instance every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.company_id, user?.department_id, isSuperAdmin]);

    const classOptions = useMemo(() => {
        if (isSuperAdmin) {
            return allClasses.map((c) => ({
                value: c.class_name,
                label: c.class_name,
            }));
        }
        const uniq = [...new Set(assignments.map((a) => a.class).filter(Boolean))];
        return uniq.map((name) => ({ value: name, label: name }));
    }, [isSuperAdmin, allClasses, assignments]);

    const divisionOptions = useMemo(() => {
        if (isSuperAdmin) {
            return allDivisions.map((d) => ({
                value: d.name,
                label: d.name,
            }));
        }
        const forClass = assignments.filter((a) =>
            selectedClass ? a.class === selectedClass : true
        );
        const uniq = [...new Set(forClass.map((a) => a.division).filter(Boolean))];
        return uniq.map((name) => ({ value: name, label: name }));
    }, [isSuperAdmin, allDivisions, assignments, selectedClass]);

    const onClassChange = (value) => {
        setSelectedClass(value);
        if (isSuperAdmin) {
            return;
        }
        const match = assignments.filter((a) => a.class === value);
        if (match.length === 1) {
            setSelectedDivision(match[0].division);
        } else {
            setSelectedDivision('');
        }
    };

    const onDivisionChange = (value) => {
        setSelectedDivision(value);
    };

    /** After send: keep class/division for teachers; clear for admin. */
    const resetClassDivisionAfterSend = () => {
        if (isSuperAdmin) {
            setSelectedClass('');
            setSelectedDivision('');
            return;
        }
        if (assignments.length === 1) {
            setSelectedClass(assignments[0].class);
            setSelectedDivision(assignments[0].division);
        }
    };

    const fieldsLocked =
        !isSuperAdmin && assignments.length === 1;

    const canSelectClassDivision =
        isSuperAdmin || assignments.length > 0;

    return {
        isSuperAdmin,
        loadingScope,
        classOptions,
        divisionOptions,
        selectedClass,
        selectedDivision,
        onClassChange,
        onDivisionChange,
        resetClassDivisionAfterSend,
        fieldsLocked,
        canSelectClassDivision,
        assignmentCount: assignments.length,
    };
}
